import React from 'react';
import { Camera, Phone, Mail, MapPin, Briefcase, Building2, Check, User, CreditCard, CircleAlert, Loader2 } from 'lucide-react';
import { TextField, MenuItem, Checkbox } from '@mui/material';
import UserAvatar from './UserAvatar';
import { ROLES, ADMIN_ROLES, MANAGERS } from '../utils/constants';

const SharedProfileForm = ({
    isUS,
    state,
    handleChange,
    handleCheckBoxChange,
    checked,
    updateProfile,
    editable,
    emailError,
    userSummary,
    onUploadImage,
    isUploadingImg,
    isLoading,
    handleCancel,
    isProfileView = false,
    submitAttempted = false,
    showValidationBanner = false
}) => {
    
    // Group UI components
    const renderSectionHeader = (icon, title) => (
        <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                {icon}
            </div>
            <h3 className="font-bold text-slate-800 text-base">{title}</h3>
        </div>
    );

    const renderInput = (label, name, value, type = "text", required = false, isSelect = false, options = [], error = null, forceDisabled = false) => {
        const isDisabled = editable || forceDisabled;
        if (isSelect) {
            return (
                <div className={`w-full ${isDisabled ? 'cursor-not-allowed opacity-75' : ''}`}>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>
                    <TextField 
                        error={submitAttempted && required && !value}
                        select 
                        size="small" 
                        name={name} 
                        value={value || ""} 
                        onChange={handleChange} 
                        required={required} 
                        disabled={isDisabled}
                        className={`w-full bg-white ${submitAttempted && required && !value ? '[&_fieldset]:!border-red-500' : ''} ${isDisabled ? 'pointer-events-none' : ''}`} 
                        sx={{ '& .MuiOutlinedInput-root': { minHeight: '40px', borderRadius: '8px' }, '& .MuiSelect-select': { paddingTop: '8px', paddingBottom: '8px', fontSize: '14px', cursor: isDisabled ? 'not-allowed' : 'pointer' } }}
                        SelectProps={{ displayEmpty: true }}
                    >
                        <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5, color: '#94a3b8' }}>Select {label}</MenuItem>
                        {options.map((opt, i) => (
                            <MenuItem key={i} value={opt.value} sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
                </div>
            );
        }
        
        return (
            <div className={`w-full ${isDisabled ? 'cursor-not-allowed opacity-75' : ''}`}>
                <label className="block text-xs font-semibold text-slate-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>
                <TextField 
                    error={submitAttempted && required && !value}
                    type={type}
                    size="small" 
                    name={name} 
                    value={value || ""} 
                    onChange={handleChange} 
                    required={required} 
                    disabled={isDisabled}
                    placeholder={`Enter ${label}`}
                    className={`w-full bg-white ${submitAttempted && required && !value ? '[&_fieldset]:!border-red-500' : ''} ${isDisabled ? 'pointer-events-none' : ''}`} 
                    sx={{ '& .MuiOutlinedInput-root': { minHeight: '40px', borderRadius: '8px', fontSize: '14px', cursor: isDisabled ? 'not-allowed' : 'text' }, '& input': { cursor: isDisabled ? 'not-allowed' : 'text' } }}
                />
                {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            </div>
        );
    };

    return (
        <div className={isProfileView ? "flex flex-col lg:flex-row w-full px-4 lg:px-6 pb-4 lg:pb-6 gap-3 items-start" : "w-full flex-1 flex flex-col gap-3"}>
            
            {/* LEFT SIDEBAR */}
            {isProfileView && (
                 <div className="w-full lg:w-[300px] flex-shrink-0 lg:sticky lg:top-[0px] flex flex-col gap-3 lg:max-h-[calc(100vh-75px)]">
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col overflow-y-auto custom-scrollbar relative"> {/* Top section: Avatar & Name */}
                    <div className="p-6 flex flex-col items-center shrink-0">
                        <div className="relative mb-4 group cursor-pointer" 
                        onClick={(e)=>{
                            if(isUploadingImg) return;
                            onUploadImage(e)
                        }}>
                           <div className="relative w-28 h-28">
                            <UserAvatar
                                name={userSummary.name}
                                imageUrl={userSummary.avatar}
                                size={112}
                                className="border-4 border-blue-50"
                            />

                            {isUploadingImg && (
                                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-medium shadow-lg flex items-center gap-2">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    Updating
                                </div>
                            )}
                        </div>
                            
                            {onUploadImage && !isUploadingImg && (
                                <div className="absolute bottom-0 right-0 p-2 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors">
                                    <Camera size={16} />
                                </div>
                            )}
                        </div>
                        
                        <h2 className="text-xl font-bold text-slate-800 text-center">{userSummary.name || "Employee Name"}</h2>
                        <p className="text-blue-600 font-medium text-sm text-center mt-1">{userSummary.designation || "Designation"}</p>
                    </div>

                    {/* Bottom section: Details */}
                    <div className="p-6 pt-0 space-y-5 shrink-0">
                        <div className="flex items-start gap-3">
                            <User size={18} className="text-slate-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-slate-500 font-medium">Employee ID</p>
                                <p className="text-sm font-semibold text-slate-800">{userSummary.empId || "-"}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <MapPin size={18} className="text-slate-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-slate-500 font-medium">Location</p>
                                <p className="text-sm font-semibold text-slate-800">{userSummary.location || "-"}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Check size={18} className="text-slate-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-slate-500 font-medium mb-1">Status</p>
                                <span className={`px-2.5 py-0.5 rounded-md text-xs font-semibold ${userSummary.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                                    {userSummary.status || "Unknown"}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Briefcase size={18} className="text-slate-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-slate-500 font-medium">Date of Joining</p>
                                <p className="text-sm font-semibold text-slate-800">{userSummary.doj || "-"}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <User size={18} className="text-slate-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-slate-500 font-medium">Reporting Manager</p>
                                <p className="text-sm font-semibold text-slate-800 break-all">{userSummary.manager || "-"}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Mail size={18} className="text-slate-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-slate-500 font-medium">Work Email</p>
                                <p className="text-sm font-semibold text-slate-800 break-all">{userSummary.workEmail || "-"}</p>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            )}

            {/* RIGHT FORM CONTENT */}
            <div className={`flex-1 flex flex-col gap-3 ${isProfileView ? 'lg:pr-2' : ''}`}>
                {showValidationBanner && (
                    <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 shrink-0">
                        <CircleAlert size={16} />
                        <span>Please complete all required fields before updating the profile.</span>
                    </div>
                )}
                
                {/* Personal Information */}
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    {renderSectionHeader(<User size={20} />, "Personal Information")}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {renderInput("First Name", isUS ? "fName" : "firstName", isUS ? state.fName : state.firstName, "text", true)}
                        {renderInput("Last Name", isUS ? "lName" : "lastName", isUS ? state.lName : state.lastName, "text", true)}
                        {renderInput("Date of Birth", "dob", state.dob, "date", true)}
                        {renderInput("Father's Name", "fatherName", state.fatherName, "text", true)}
                        {renderInput("Blood Group", "bloodGrp", state.bloodGrp, "text", true)}
                        {renderInput("Original Birthday", "originalDob", state.originalDob, "date")}
                    </div>
                </div>

                {/* Contact & Address Information */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex-1">
                        {renderSectionHeader(<Phone size={20} />, "Contact Information")}
                        <div className="space-y-4">
                            {renderInput("Phone Number", "phone", state.phone, "text", true)}
                            {renderInput("Personal Email ID", "email", state.email, "email", true, false, [], emailError !== "Valid" ? emailError : null)}
                            {renderInput("Emergency Contact No.", "contactNo", state.contactNo, "text", true)}
                        </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex-1">
                        {renderSectionHeader(<MapPin size={20} />, "Address Information")}
                        <div className="space-y-4">
                            {renderInput("Address Line 1", "addrLine1", state.addrLine1, "text", true)}
                            {renderInput("Address Line 2", "addrLine2", state.addrLine2, "text", true)}
                            <div className="grid grid-cols-2 gap-3">
                                {renderInput("City", "city", state.city, "text", true)}
                                {renderInput("State", "empstate", state.empstate, "text")}
                            </div>
                            <div className="w-1/2 pr-2">
                                {renderInput("Pin Code", "pincode", state.pincode, "text", true)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Employment Information */}
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    {renderSectionHeader(<Briefcase size={20} />, "Employment Information")}
                    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
                        {renderInput("Employee Status", "status", state.status, "text", true, true, [{label: 'Active', value: 'Active'}, {label: 'Inactive', value: 'Inactive'}], null, true)}
                        {renderInput("Designation", "designation", state.designation, "text", true, false, [], null, true)}
                        {renderInput("Role", "role", state.role, "text", true, true, [...ROLES,...ADMIN_ROLES], null, true)}
                        {renderInput("Date of Joining", "doj", state.doj, "date", true, false, [], null, true)}
                        {renderInput("Reporting Manager", "manager", state.manager, "text", true, true, MANAGERS, null, true)}
                        
                        <div className="w-full">
                            <label className="block text-xs font-semibold text-slate-700 mb-1">Remote Employee?</label>
                            <div className="flex items-center gap-4 mt-2">
                                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                    <input type="radio" name="remote" value="Yes" checked={state.remote === "Yes"} onChange={handleChange} disabled={editable} className="w-4 h-4 text-blue-600 focus:ring-blue-600 border-gray-300" />
                                    Yes
                                </label>
                                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                    <input type="radio" name="remote" value="No" checked={state.remote === "No"} onChange={handleChange} disabled={editable} className="w-4 h-4 text-blue-600 focus:ring-blue-600 border-gray-300" />
                                    No
                                </label>
                            </div>
                        </div>
                        
                        {isUS && (
                            <div className="w-full">
                                <label className="block text-xs font-semibold text-slate-700 mb-1">Has Passport?</label>
                                <div className="flex items-center gap-4 mt-2">
                                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                        <input type="radio" name="hasPassport" value="Yes" checked={state.hasPassport === "Yes"} onChange={handleChange} disabled={editable} className="w-4 h-4 text-blue-600 focus:ring-blue-600 border-gray-300" />
                                        Yes
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                        <input type="radio" name="hasPassport" value="No" checked={state.hasPassport === "No"} onChange={handleChange} disabled={editable} className="w-4 h-4 text-blue-600 focus:ring-blue-600 border-gray-300" />
                                        No
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Payroll & Banking Information */}
                <div className={`bg-white border border-slate-200 rounded-2xl p-5 shadow-sm ${isProfileView ? 'mb-3' : ''}`}>
                    {renderSectionHeader(<CreditCard size={20} />, "Payroll & Banking Information")}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {isUS ? (
                            <>
                                {renderInput("SSN Number", "ssn", state.ssn, "text", true)}
                                {renderInput("Passport Number", "passportNo", state.passportNo, "text")}
                                {renderInput("Passport Expiry", "expiryDt", state.expiryDt, "date")}
                                {renderInput("Account Number", "accountNo", state.accountNo, "text", true)}
                            </>
                        ) : (
                            <>
                                {renderInput("Aadhar Number", "aadharNo", state.aadharNo, "text", true)}
                                {renderInput("PAN Number", "panNo", state.panNo, "text", true)}
                                {renderInput("Name as in Bank", "nameAsBank", state.nameAsBank, "text", true)}
                                {renderInput("Bank Name", "bank", state.bank, "text", true)}
                                {renderInput("Account Number", "accountNo", state.accountNo, "text", true)}
                                {renderInput("IFSC Code", "ifsc", state.ifsc, "text", true)}
                                {renderInput("PF Account Number", "uanPF", state.uanPF, "text", true)}
                            </>
                        )}
                    </div>
                </div>

                {/* Footer Action Bar */}
                {!editable && (
                    <div className="mt-auto bg-white border border-slate-200 rounded-xl py-2.5 px-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3 sticky bottom-4 z-10">
                        <div className="flex flex-col">
                            {submitAttempted && showValidationBanner && (
                                <div className="flex items-center gap-1.5 text-xs font-medium text-red-600 mb-1">
                                    <CircleAlert size={14} />
                                    <span>Please complete all required fields before updating the details.</span>
                                </div>
                            )}
                            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer font-medium select-none">
                                <Checkbox 
                                    checked={checked}
                                    onChange={handleCheckBoxChange}
                                    sx={{ '&.Mui-checked': { color: '#2563eb' } }}
                                    size="small"
                                    style={{ padding: '4px' }}
                                />
                                I ensure that all the details are correct to the best of my knowledge.
                            </label>
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                            <button 
                                type="button" 
                                onClick={handleCancel}
                                className="px-4 py-1.5 text-sm border border-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors w-full sm:w-auto"
                            >
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                disabled={isLoading || !checked}
                                onClick={updateProfile}
                                className={`px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg font-semibold transition-colors shadow-sm w-full sm:w-auto flex items-center justify-center ${(!checked || isLoading) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                            >
                                {isLoading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span> : null}
                                Save Profile
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SharedProfileForm;